'use client';

import { useLondonNow } from '@/components/chrome/useLondonNow';
import {
  DAY_NAMES,
  findException,
  formatDayHours,
  formatDayMonth,
  londonParts,
  upcomingExceptions,
} from '@/lib/hours';
import { HOURS_EXCEPTIONS, WEEK_HOURS } from '@/lib/data/hours';

// Monday-first display order over the Sunday-indexed data.
const ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/**
 * The weekly hours, with today marked and today's exception applied, read from
 * the same clock and the same exception list as the nav status so the two can
 * never disagree on the same screen.
 */
export function HoursTable() {
  const now = useLondonNow();
  const today = now ? londonParts(now) : null;
  const exception = today ? findException(HOURS_EXCEPTIONS, today.iso) : undefined;
  const upcoming = today ? upcomingExceptions(HOURS_EXCEPTIONS, today.iso) : [];

  return (
    <>
      <table className="w-full border-collapse text-left">
        <caption className="sr-only">Opening hours</caption>
        <tbody>
          {ORDER.map((index) => {
            const isToday = today?.weekday === index;
            const hours = isToday && exception ? exception.hours : (WEEK_HOURS[index] ?? null);
            return (
              <tr
                key={index}
                data-today={isToday ? 'true' : undefined}
                className={`border-b border-ink/13 ${isToday ? 'text-ink' : 'text-ink-soft'}`}
              >
                <th
                  scope="row"
                  className={`py-3 text-[13px] ${isToday ? 'font-medium' : 'font-normal'}`}
                >
                  {DAY_NAMES[index]}
                  {isToday && <span className="sr-only"> (today)</span>}
                </th>
                <td className="py-3 text-right text-[13px] tabular-nums">
                  {formatDayHours(hours)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {exception?.note && (
        <p className="mt-3 text-[12px] leading-[1.6] text-brick">{exception.note}</p>
      )}
      {upcoming.length > 0 && (
        <ul className="mt-4 space-y-1 text-[12px] leading-[1.6] text-ink-soft">
          {upcoming.map(({ iso, exception: coming }) => (
            <li key={iso}>
              {formatDayMonth(iso)}: {formatDayHours(coming.hours)}
              {coming.note ? `. ${coming.note}` : ''}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
